import { useState, useEffect, useRef } from 'react';
import { Card } from '../App';
import { X, RotateCw } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { ManaSymbol } from './ManaSymbol';
import { probabilityAtLeastOne, cardsSeenByTurn, getProbabilityColorClass, getProbabilityBgClass, formatProbability, hypergeometricCDFTail } from '../lib/probability';
import { parseManaCost } from '../lib/mana';
import { useResolvedCardImage } from '../lib/scryfall';

interface PlanColors {
  [key: string]: { hex: string; bg: string; text: string; border: string };
}

interface CardProfileModalProps {
  card: Card;
  onClose: () => void;
  onSave: (updates: Partial<Card>) => void;
  onUpdateQuantity?: (quantity: number) => void;
  onMakeCommander?: (card: Card) => void;
  onMakeCompanion?: (card: Card) => void;
  planColors?: PlanColors;
  // Probability props
  deckSize?: number;
  landCount?: number;
  onThePlay?: boolean;
  // Current viewing match (passed from parent)
  activeMatch?: string;
}

// Match colors (formerly Plans A-E, now Match 1-5)
const defaultPlanColors: PlanColors = {
  '1': { hex: '#22d3ee', bg: 'rgba(34,211,238,0.2)', text: '#22d3ee', border: 'rgba(34,211,238,0.3)' },
  '2': { hex: '#34d399', bg: 'rgba(52,211,153,0.2)', text: '#34d399', border: 'rgba(52,211,153,0.3)' },
  '3': { hex: '#fbbf24', bg: 'rgba(251,191,36,0.2)', text: '#fbbf24', border: 'rgba(251,191,36,0.3)' },
  '4': { hex: '#a855f7', bg: 'rgba(168,85,247,0.2)', text: '#a855f7', border: 'rgba(168,85,247,0.3)' },
  '5': { hex: '#fb7185', bg: 'rgba(251,113,133,0.2)', text: '#fb7185', border: 'rgba(251,113,133,0.3)' },
  // Backwards compatibility with old Plan letters
  A: { hex: '#22d3ee', bg: 'rgba(34,211,238,0.2)', text: '#22d3ee', border: 'rgba(34,211,238,0.3)' },
  B: { hex: '#34d399', bg: 'rgba(52,211,153,0.2)', text: '#34d399', border: 'rgba(52,211,153,0.3)' },
  C: { hex: '#fbbf24', bg: 'rgba(251,191,36,0.2)', text: '#fbbf24', border: 'rgba(251,191,36,0.3)' },
  D: { hex: '#a855f7', bg: 'rgba(168,85,247,0.2)', text: '#a855f7', border: 'rgba(168,85,247,0.3)' },
  E: { hex: '#fb7185', bg: 'rgba(251,113,133,0.2)', text: '#fb7185', border: 'rgba(251,113,133,0.3)' },
};

export function CardProfileModal({ card, onClose, onSave, onUpdateQuantity, onMakeCommander, onMakeCompanion, planColors = defaultPlanColors, deckSize = 60, landCount = 24, onThePlay = true, activeMatch }: CardProfileModalProps) {
  const [rampType, setRampType] = useState(card.rampType || 'None');
  const [rampCategory, setRampCategory] = useState<'R1' | 'R2' | 'R3' | 'R4' | 'R5' | 'R6' | 'none'>(card.rampCategory || 'none');
  const [rampTurn, setRampTurn] = useState(card.rampTurn?.toString() || '');
  const [rampNet, setRampNet] = useState(card.rampNet?.toString() || '');
  const [cantrip, setCantrip] = useState(card.cantrip?.toString() || '');

  // Bonus fields state
  const [showBonusFields, setShowBonusFields] = useState(false);
  const [surveilAmount, setSurveilAmount] = useState(card.surveilAmount?.toString() || '');
  const [scryAmount, setScryAmount] = useState(card.scryAmount?.toString() || '');
  const [millAmount, setMillAmount] = useState(card.millAmount?.toString() || '');
  const [filterAmount, setFilterAmount] = useState(card.filterAmount?.toString() || '');
  const [manaMultiplier, setManaMultiplier] = useState(card.manaMultiplier?.toString() || '');

  // Double-faced card flip state
  const [showBackFace, setShowBackFace] = useState(false);

  // Resolved Scryfall image URL
  const targetName = showBackFace && card.backName ? card.backName : (card.name.includes(' // ') ? card.name.split(' // ')[0] : card.name);
  const initialUrl = showBackFace && card.backImageUrl ? card.backImageUrl : card.imageUrl;
  const cardImgSrc = useResolvedCardImage(targetName, initialUrl);

  // Probability calculator state
  const [selectedProbPlan, setSelectedProbPlan] = useState<'base' | 'A' | 'B' | 'C' | 'D' | 'E'>('base');
  const [selectedCopyIndex, setSelectedCopyIndex] = useState(0); // Which copy to analyze (0-indexed)
  const [seenCopies, setSeenCopies] = useState(1); // X - copies to see
  const [seenByTurn, setSeenByTurn] = useState(3); // Y - turn to see by
  const [castCopies, setCastCopies] = useState(1); // N - copies to cast
  const [castByTurn, setCastByTurn] = useState(card.cmc || 2); // Z - turn to cast by
  const [probOnPlay, setProbOnPlay] = useState(onThePlay);

  // Matches (formerly Plans A-E, now Match 1-5)
  const matches = ['1', '2', '3', '4', '5'] as const;
  // Keep plans for backwards compatibility with data structure
  const plans = ['A', 'B', 'C', 'D', 'E'] as const;
  const allPlans = ['base', 'A', 'B', 'C', 'D', 'E'] as const;
  const baseMV = card.effectiveMV ?? card.cmc;

  // Map match numbers to plan letters for data compatibility
  const matchToPlan = (match: string): 'A' | 'B' | 'C' | 'D' | 'E' => {
    const map: Record<string, 'A' | 'B' | 'C' | 'D' | 'E'> = { '1': 'A', '2': 'B', '3': 'C', '4': 'D', '5': 'E' };
    return map[match] || 'A';
  };

  // Check if card is eligible to be a commander
  // Includes: Legendary Creatures, some Planeswalkers, and Background enchantments
  const canBeCommander = (
    (card.type.includes('Legendary') && card.type.includes('Creature')) ||
    (card.type.includes('Planeswalker') && card.type.toLowerCase().includes('can be your commander')) ||
    card.type.includes('Legendary Planeswalker') ||
    card.isBackground  // Background enchantments can go in Command Zone
  ) && !card.isCommander;

  // Check if card is eligible to be a companion
  // Card must have the Companion keyword and not already be set as companion
  const canBeCompanion = card.isCompanion !== true && !!card.companionCondition;

  // Per-copy MVs for each plan
  const [planCopyMVs, setPlanCopyMVs] = useState<Record<string, number[]>>(() => {
    const initial: Record<string, number[]> = {};
    plans.forEach(plan => {
      const saved = card.planCopyMVs?.[plan];
      // Fill sparse arrays - ensure all indices have a value (use baseMV for missing entries)
      initial[plan] = Array(card.quantity).fill(baseMV).map((def, i) => saved?.[i] ?? def);
    });
    return initial;
  });

  // Per-copy mana cost strings for each plan (e.g., '2W', '1WW', 'W/U')
  const [planCopyCosts, setPlanCopyCosts] = useState<Record<string, string[]>>(() => {
    const initial: Record<string, string[]> = {};
    // Strip adventure/split " // " portion - only use front face cost
    const rawCost = card.manaCost?.includes(' // ') ? card.manaCost.split(' // ')[0] : card.manaCost;
    const defaultCost = rawCost?.replace(/[{}]/g, '') || String(baseMV);
    plans.forEach(plan => {
      const saved = card.planCopyCosts?.[plan];
      // Fill sparse arrays - ensure all indices have a value (use defaultCost for missing entries)
      initial[plan] = Array(card.quantity).fill(defaultCost).map((def, i) => saved?.[i] ?? def);
    });
    return initial;
  });

  // Per-copy sideboard status for each plan (true = in sideboard)
  const [planCopySideboard, setPlanCopySideboard] = useState<Record<string, boolean[]>>(() => {
    const initial: Record<string, boolean[]> = {};
    plans.forEach(plan => {
      const saved = card.planCopySideboard?.[plan];
      // Fill sparse arrays - ensure all indices have a value (use false for missing entries)
      initial[plan] = Array(card.quantity).fill(false).map((def, i) => saved?.[i] ?? def);
    });
    return initial;
  });

  // Track which plans have pending (unsaved) changes - for ORANGE indicator
  const [pendingPlans, setPendingPlans] = useState<Set<string>>(new Set());
  // Track which plans have been secured (saved) during this session - for GREEN indicator
  const [securedPlans, setSecuredPlans] = useState<Set<string>>(new Set());
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // When editing, mark plan as pending (orange)
  const triggerAutoSave = (plan: string) => {
    // Mark this plan as pending
    setPendingPlans(prev => new Set(prev).add(plan));

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new auto-save timer (2 seconds)
    autoSaveTimerRef.current = setTimeout(() => {
      // Timer expired - indicator stays orange until Save
    }, 2000);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Parse mana cost string to calculate MV
  // Examples: '2W' = 3, '1WW' = 3, 'W/U' = 1 (hybrid counts as 1), 'WUBRG' = 5
  const parseCostToMV = (costStr: string): number => {
    if (!costStr || costStr === '0') return 0;
    let mv = 0;
    // Match numbers (generic mana)
    const numbers = costStr.match(/\d+/g);
    if (numbers) {
      mv += numbers.reduce((sum, n) => sum + parseInt(n), 0);
    }
    // Match color pips (W, U, B, R, G, C) - each counts as 1
    // Also handle hybrids like W/U, 2/W - count as 1 each
    const colorPips = costStr.replace(/\d+/g, '').replace(/\//g, '').length;
    // But hybrids with / should count as 1, not 2
    const slashCount = (costStr.match(/\//g) || []).length;
    mv += colorPips - slashCount;
    return Math.max(0, mv);
  };

  const handleCopyCostChange = (plan: string, copyIndex: number, value: string) => {
    // Update the cost string
    const upperValue = value.toUpperCase().replace(/[^0-9WUBRGC\/]/g, '');
    setPlanCopyCosts(prev => ({
      ...prev,
      [plan]: prev[plan].map((cost, i) => i === copyIndex ? upperValue : cost)
    }));
    // Also update MV based on parsed cost
    const newMV = parseCostToMV(upperValue);
    setPlanCopyMVs(prev => ({
      ...prev,
      [plan]: prev[plan].map((mv, i) => i === copyIndex ? newMV : mv)
    }));
    // Mark as pending (orange indicator)
    triggerAutoSave(plan);
    // Auto-sync Probability Calculator to show the match being edited
    if (['A', 'B', 'C', 'D', 'E'].includes(plan)) {
      setSelectedProbPlan(plan as 'A' | 'B' | 'C' | 'D' | 'E');
      setSelectedCopyIndex(copyIndex);
    }
  };

  const toggleCopySideboard = (plan: string, copyIndex: number) => {
    setPlanCopySideboard(prev => ({
      ...prev,
      [plan]: prev[plan].map((inSB, i) => i === copyIndex ? !inSB : inSB)
    }));
    // Mark as pending (orange indicator)
    triggerAutoSave(plan);
    // Auto-sync Probability Calculator to show the match being edited
    if (['A', 'B', 'C', 'D', 'E'].includes(plan)) {
      setSelectedProbPlan(plan as 'A' | 'B' | 'C' | 'D' | 'E');
      setSelectedCopyIndex(copyIndex);
    }
  };

  // State for "Close?" prompt after saving
  const [showClosePrompt, setShowClosePrompt] = useState(false);

  const handleSave = () => {
    // Move all pending plans to secured (orange → green)
    setSecuredPlans(prev => {
      const updated = new Set(prev);
      pendingPlans.forEach(p => updated.add(p));
      return updated;
    });
    // Clear pending indicators
    setPendingPlans(new Set());
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    onSave({
      rampType: rampType !== 'None' ? rampType : undefined,
      rampCategory: rampCategory !== 'none' ? rampCategory : undefined,
      rampTurn: rampTurn ? parseInt(rampTurn) : undefined,
      rampNet: rampNet ? parseInt(rampNet) : undefined,
      cantrip: cantrip ? parseFloat(cantrip) : undefined,
      surveilAmount: surveilAmount ? parseInt(surveilAmount) : undefined,
      scryAmount: scryAmount ? parseInt(scryAmount) : undefined,
      millAmount: millAmount ? parseInt(millAmount) : undefined,
      filterAmount: filterAmount ? parseInt(filterAmount) : undefined,
      manaMultiplier: manaMultiplier ? parseFloat(manaMultiplier) : undefined,
      planCopyMVs: planCopyMVs as Card['planCopyMVs'],
      planCopySideboard: planCopySideboard as Card['planCopySideboard'],
      planCopyCosts: planCopyCosts as Card['planCopyCosts'],
    });
    // Show close prompt
    setShowClosePrompt(true);
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-[#0a0f1a] border border-white/20 rounded-xl w-auto max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <span className="text-white font-medium">{showBackFace && card.backName ? card.backName : card.name}</span>
            {/* Quantity controls */}
            {onUpdateQuantity ? (
              <div className="flex items-center gap-1 bg-white/5 rounded px-1.5 py-0.5 border border-white/10">
                <button
                  onClick={() => onUpdateQuantity(Math.max(1, card.quantity - 1))}
                  className="w-5 h-5 text-white/40 hover:text-white hover:bg-white/10 rounded text-sm flex items-center justify-center"
                  title="Decrease quantity"
                >
                  −
                </button>
                <span className="text-[11px] text-white/60 w-5 text-center font-mono">{card.quantity}</span>
                <button
                  onClick={() => onUpdateQuantity(card.quantity + 1)}
                  className="w-5 h-5 text-white/40 hover:text-white hover:bg-white/10 rounded text-sm flex items-center justify-center"
                  title="Increase quantity"
                >
                  +
                </button>
                <button
                  onClick={() => onUpdateQuantity(4)}
                  className="text-[10px] px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 rounded font-medium transition-colors ml-0.5"
                  title="Fill playset (4 copies)"
                >
                  ×4
                </button>
              </div>
            ) : (
              <span className="text-[10px] text-white/40 px-1.5 py-0.5 bg-white/5 rounded">×{card.quantity}</span>
            )}
            <span className="text-[10px] text-white/30">@ MV {baseMV}</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Active Match Indicator - Circle 1 */}
            {activeMatch && activeMatch !== 'base' && (
              <div
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs font-bold"
                style={{
                  backgroundColor: planColors[activeMatch]?.bg || 'rgba(255,255,255,0.1)',
                  borderColor: planColors[activeMatch]?.border || 'rgba(255,255,255,0.2)',
                  color: planColors[activeMatch]?.text || '#fff'
                }}
                title={`Currently viewing Match ${activeMatch === 'A' ? '1' : activeMatch === 'B' ? '2' : activeMatch === 'C' ? '3' : activeMatch === 'D' ? '4' : '5'}`}
              >
                <span>MATCH</span>
                <span className="text-sm">{activeMatch === 'A' ? '1' : activeMatch === 'B' ? '2' : activeMatch === 'C' ? '3' : activeMatch === 'D' ? '4' : '5'}</span>
              </div>
            )}
            {activeMatch === 'base' && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-white/20 bg-white/5 text-xs font-bold text-white/60">
                BASE VIEW
              </div>
            )}
            <button onClick={onClose} className="text-white/40 hover:text-white p-1">
              <X className="size-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex gap-4 p-4">
          {/* Card Image - larger display with flip button for MDFC/Transform */}
          <div className="w-[220px] flex-shrink-0">
            <div className="rounded-lg overflow-hidden border border-white/10 relative">
              <ImageWithFallback
                src={cardImgSrc}
                alt={showBackFace && card.backName ? card.backName : card.name}
                className="w-full h-auto transition-transform duration-300"
              />
              {/* Flip Button - Circle 2: MDFC and transform cards need FLIP button */}
              {card.isDoubleFaced && card.backImageUrl && (
                <button
                  onClick={() => setShowBackFace(!showBackFace)}
                  className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 bg-black/70 hover:bg-black/90 text-white rounded-lg border border-white/30 text-xs font-medium transition-all hover:scale-105"
                  title={showBackFace ? 'Show front face' : 'Show back face'}
                >
                  <RotateCw className="size-3" />
                  <span>FLIP</span>
                </button>
              )}
            </div>
            {/* Face indicator for double-faced cards */}
            {card.isDoubleFaced && (
              <div className="flex justify-center gap-2 mt-2">
                <button
                  onClick={() => setShowBackFace(false)}
                  className={`px-2 py-0.5 text-[9px] rounded transition-colors ${!showBackFace
                    ? 'bg-cyan-500/30 text-cyan-400 border border-cyan-500/50'
                    : 'bg-white/5 text-white/40 border border-white/10 hover:text-white/60'
                    }`}
                >
                  Front
                </button>
                <button
                  onClick={() => setShowBackFace(true)}
                  className={`px-2 py-0.5 text-[9px] rounded transition-colors ${showBackFace
                    ? 'bg-cyan-500/30 text-cyan-400 border border-cyan-500/50'
                    : 'bg-white/5 text-white/40 border border-white/10 hover:text-white/60'
                    }`}
                >
                  Back
                </button>
              </div>
            )}

            {/* Mana Cost Legend - Input Guide */}
            <div className="mt-3 bg-white/5 rounded-lg p-2 border border-white/10">
              <div className="text-[8px] text-white/40 text-center mb-1">Cost Input Guide</div>
              <div className="flex flex-wrap items-center justify-center gap-1 text-[8px]">
                {/* Basic colors */}
                {['W', 'U', 'B', 'R', 'G', 'C'].map(c => (
                  <span key={c} className="flex items-center gap-0.5 bg-white/5 px-1 py-0.5 rounded">
                    <ManaSymbol symbol={c} size="sm" />
                    <span className="font-mono text-white/60">{c}</span>
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap items-center justify-center gap-1 text-[8px] mt-1">
                {/* Special */}
                <span className="flex items-center gap-0.5 bg-white/5 px-1 py-0.5 rounded" title="Hybrid: W/U, B/G, 2/W">
                  <ManaSymbol symbol="WU" size="sm" />
                  <span className="font-mono text-white/60">W/U</span>
                </span>
                <span className="flex items-center gap-0.5 bg-white/5 px-1 py-0.5 rounded" title="Phyrexian: W/P, U/P">
                  <ManaSymbol symbol="WP" size="sm" />
                  <span className="font-mono text-white/60">W/P</span>
                </span>
                <span className="flex items-center gap-0.5 bg-white/5 px-1 py-0.5 rounded" title="Generic: 1, 2, 3...">
                  <ManaSymbol symbol="2" size="sm" />
                  <span className="font-mono text-white/60">#</span>
                </span>
                <span className="flex items-center gap-0.5 bg-white/5 px-1 py-0.5 rounded" title="X mana">
                  <ManaSymbol symbol="X" size="sm" />
                  <span className="font-mono text-white/60">X</span>
                </span>
              </div>
            </div>
          </div>

          {/* Properties */}
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {/* Ramp + Cantrip + Bonus */}
            <div className="bg-white/5 rounded px-2 py-1.5 border border-white/10">
              <div className="flex items-center gap-2 text-[10px] flex-wrap">
                <span className="text-white/40">RAMP</span>
                <select
                  value={rampType}
                  onChange={(e) => {
                    setRampType(e.target.value);
                    // Auto-set category based on type
                    if (e.target.value === 'None') setRampCategory('none');
                  }}
                  className="bg-white/10 border border-white/10 rounded px-1.5 py-0.5 text-[10px] text-white"
                >
                  <option value="None">None</option>
                  <option value="Creature">Creature</option>
                  <option value="Artifact">Artifact</option>
                  <option value="Spell">Spell</option>
                  <option value="Land">Land</option>
                </select>
                {/* R1-R6 Category Picker */}
                {rampType !== 'None' && (
                  <select
                    value={rampCategory}
                    onChange={(e) => setRampCategory(e.target.value as 'R1' | 'R2' | 'R3' | 'R4' | 'R5' | 'R6' | 'none')}
                    className="bg-amber-500/20 border border-amber-500/30 rounded px-1.5 py-0.5 text-[10px] text-amber-400 font-medium"
                    title="Ramp Primitive Category (R1-R6)"
                  >
                    <option value="none">─ Category ─</option>
                    <option value="R1">R1: Extra Land Plays</option>
                    <option value="R2">R2: Land to Battlefield</option>
                    <option value="R3">R3: Land Tutors</option>
                    <option value="R4">R4: Mana Sources</option>
                    <option value="R5">R5: Multipliers</option>
                    <option value="R6">R6: Timing Mods</option>
                  </select>
                )}
                <input
                  type="number"
                  value={rampNet}
                  onChange={(e) => setRampNet(e.target.value)}
                  placeholder="N"
                  disabled={rampType === 'None'}
                  className="w-7 bg-white/15 border border-white/25 rounded px-1 py-0.5 text-[10px] text-white font-medium text-center disabled:opacity-30"
                  title="Net mana produced per turn"
                />
                <input
                  type="number"
                  value={rampTurn}
                  onChange={(e) => setRampTurn(e.target.value)}
                  placeholder="T"
                  disabled={rampType === 'None'}
                  className="w-7 bg-white/15 border border-white/25 rounded px-1 py-0.5 text-[10px] text-white font-medium text-center disabled:opacity-30"
                  title="Turn when ramp comes online"
                />
                <span className="text-white/20">|</span>
                <span className="text-white/40">DRAW</span>
                <input
                  type="number"
                  step="0.5"
                  value={cantrip}
                  onChange={(e) => setCantrip(e.target.value)}
                  placeholder="0"
                  className="w-8 bg-white/10 border border-white/20 rounded px-1 py-0.5 text-[10px] text-white font-medium text-center"
                  title="Extra cards drawn (affects probability)"
                />
                <span className="text-white/20">|</span>
                <button
                  onClick={() => setShowBonusFields(!showBonusFields)}
                  className={`px-2 py-0.5 text-[9px] rounded transition-colors ${showBonusFields ? 'bg-purple-500/30 text-purple-400 border border-purple-500/40' : 'bg-white/5 text-white/40 border border-white/10 hover:text-white/60'}`}
                  title="Show additional card advantage modifiers"
                >
                  {showBonusFields ? '− Bonus' : '+ Bonus'}
                </button>
              </div>

              {/* Bonus Fields - Expandable */}
              {showBonusFields && (
                <div className="mt-2 pt-2 border-t border-white/10 grid grid-cols-5 gap-2">
                  <div className="flex flex-col items-center">
                    <span className="text-[8px] text-white/40 mb-0.5">Surveil</span>
                    <input
                      type="number"
                      value={surveilAmount}
                      onChange={(e) => setSurveilAmount(e.target.value)}
                      placeholder="0"
                      className="w-10 bg-white/10 border border-white/10 rounded px-1 py-0.5 text-[10px] text-white text-center"
                      title="Cards surveiled (selection only)"
                    />
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-[8px] text-white/40 mb-0.5">Scry</span>
                    <input
                      type="number"
                      value={scryAmount}
                      onChange={(e) => setScryAmount(e.target.value)}
                      placeholder="0"
                      className="w-10 bg-white/10 border border-white/10 rounded px-1 py-0.5 text-[10px] text-white text-center"
                      title="Cards scried (top deck manipulation)"
                    />
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-[8px] text-white/40 mb-0.5">Mill</span>
                    <input
                      type="number"
                      value={millAmount}
                      onChange={(e) => setMillAmount(e.target.value)}
                      placeholder="0"
                      className="w-10 bg-white/10 border border-white/10 rounded px-1 py-0.5 text-[10px] text-white text-center"
                      title="Cards milled (self)"
                    />
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-[8px] text-white/40 mb-0.5">Filter</span>
                    <input
                      type="number"
                      value={filterAmount}
                      onChange={(e) => setFilterAmount(e.target.value)}
                      placeholder="0"
                      className="w-10 bg-white/10 border border-white/10 rounded px-1 py-0.5 text-[10px] text-white text-center"
                      title="Filter (draw then discard)"
                    />
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-[8px] text-amber-400/70 mb-0.5">×Mana</span>
                    <input
                      type="number"
                      step="0.1"
                      value={manaMultiplier}
                      onChange={(e) => setManaMultiplier(e.target.value)}
                      placeholder="1"
                      className="w-10 bg-amber-500/10 border border-amber-500/20 rounded px-1 py-0.5 text-[10px] text-amber-400 text-center"
                      title="Mana multiplier (compounds with other creatures)"
                    />
                  </div>
                </div>
              )}

              {/* Impact explanation */}
              {(rampType !== 'None' || (cantrip && parseFloat(cantrip) > 0) || (manaMultiplier && parseFloat(manaMultiplier) !== 1)) && (
                <div className="mt-1.5 pt-1.5 border-t border-white/10 text-[9px] text-white/40">
                  {rampType !== 'None' && rampTurn && rampNet && (
                    <span className="mr-2">
                      💎 Ramp: +{rampNet} mana/turn starting T{rampTurn}
                    </span>
                  )}
                  {cantrip && parseFloat(cantrip) > 0 && (
                    <span className="text-cyan-400 mr-2">
                      📚 Draw: +{cantrip} cards → improves probabilities
                    </span>
                  )}
                  {manaMultiplier && parseFloat(manaMultiplier) !== 1 && (
                    <span className="text-amber-400">
                      ⚡ ×{manaMultiplier} mana (compounds)
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Match Overrides (Cost / Sideboard) */}
            <div className="bg-white/5 rounded px-2 py-2 border border-white/10">
              <div className="text-[9px] text-white/40 uppercase tracking-wide mb-1.5">Match Overrides (Cost / Sideboard)</div>

              {/* Header - Show Match 1-5 with override indicators */}
              <div className="flex items-center gap-1 mb-0.5">
                <span className="w-8 text-[9px] text-white/30">#</span>
                {matches.map((match, idx) => {
                  const plan = plans[idx];

                  // Check if this plan is in pending state (being edited) - ORANGE
                  const isPending = pendingPlans.has(plan);

                  // Check if this plan has been secured during this session - GREEN
                  const isSecured = securedPlans.has(plan);

                  // Check if this plan has SAVED overrides in the original card data
                  const savedSideboard = card.planCopySideboard?.[plan as 'A' | 'B' | 'C' | 'D' | 'E'] || [];
                  const savedMVs = card.planCopyMVs?.[plan as 'A' | 'B' | 'C' | 'D' | 'E'] || [];
                  const hasSavedOverride =
                    savedSideboard.some((flag: boolean) => flag === true) ||
                    savedMVs.some((mv: number) => mv !== undefined && mv !== baseMV);

                  // Show green if secured this session OR has saved overrides from before
                  const showGreen = (isSecured || hasSavedOverride) && !isPending;

                  return (
                    <div key={match} className="flex-1 text-center flex items-center justify-center gap-0.5">
                      <span className="text-[10px] font-semibold" style={{ color: planColors[plan]?.text }}>{match}</span>
                      {/* Pending indicator - ORANGE pulsing */}
                      {isPending && (
                        <span
                          className="w-2.5 h-2.5 rounded-full animate-pulse"
                          style={{
                            backgroundColor: '#f97316',
                            boxShadow: '0 0 8px #f97316'
                          }}
                          title={`Match ${match}: Unsaved changes - click Save to secure`}
                        />
                      )}
                      {/* Saved/Secured indicator - GREEN glowing bulb */}
                      {showGreen && (
                        <span
                          className="w-2.5 h-2.5 rounded-full shadow-lg"
                          style={{
                            backgroundColor: '#22c55e',
                            boxShadow: '0 0 6px #22c55e'
                          }}
                          title={`Match ${match}: Overrides saved ✓`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Reset row - appears only if any match has overrides */}
              {plans.some(p => {
                const sb = planCopySideboard[p] || [];
                const mvs = planCopyMVs[p] || [];
                return sb.some((f: boolean) => f) || mvs.some((m: number) => m !== undefined && m !== baseMV);
              }) && (
                  <div className="flex items-center gap-1 mb-1">
                    <span className="w-8 text-[7px] text-white/20"></span>
                    {plans.map(plan => {
                      const currentSideboard = planCopySideboard[plan] || [];
                      const currentMVs = planCopyMVs[plan] || [];
                      const hasOverride =
                        currentSideboard.some((flag: boolean) => flag === true) ||
                        currentMVs.some((mv: number) => mv !== undefined && mv !== baseMV);

                      if (!hasOverride) return <div key={plan} className="flex-1" />;

                      return (
                        <div key={plan} className="flex-1 text-center">
                          <button
                            onClick={() => {
                              // Reset this plan to default (no overrides)
                              setPlanCopySideboard(prev => ({
                                ...prev,
                                [plan]: Array(card.quantity).fill(false)
                              }));
                              setPlanCopyMVs(prev => ({
                                ...prev,
                                [plan]: Array(card.quantity).fill(baseMV)
                              }));
                              setPlanCopyCosts(prev => ({
                                ...prev,
                                [plan]: Array(card.quantity).fill((card.manaCost?.includes(' // ') ? card.manaCost.split(' // ')[0] : card.manaCost)?.replace(/[{}]/g, '') || String(baseMV))
                              }));
                            }}
                            className="text-[7px] px-1.5 py-0 text-red-400/60 hover:text-red-400 hover:bg-red-500/20 rounded transition-colors"
                            title={`Reset Match ${plan === 'A' ? '1' : plan === 'B' ? '2' : plan === 'C' ? '3' : plan === 'D' ? '4' : '5'} to default`}
                          >
                            reset
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

              {/* Rows */}
              {Array.from({ length: card.quantity }, (_, i) => (
                <div key={i} className="flex items-center gap-1 mb-1">
                  <span className="w-8 text-[9px] text-white/40">{i + 1}</span>
                  {plans.map(plan => {
                    const mv = planCopyMVs[plan]?.[i] ?? baseMV;
                    const inSideboard = planCopySideboard[plan]?.[i] ?? false;
                    const isOverridden = mv !== baseMV || inSideboard;
                    const c = planColors[plan];

                    return (
                      <div key={plan} className="flex-1 flex items-center gap-0.5">
                        <input
                          type="text"
                          value={planCopyCosts[plan]?.[i] ?? (card.manaCost?.includes(' // ') ? card.manaCost.split(' // ')[0] : card.manaCost)?.replace(/[{}]/g, '') ?? ''}
                          onChange={(e) => handleCopyCostChange(plan, i, e.target.value)}
                          placeholder="2W"
                          className="w-full text-center text-[11px] font-mono rounded py-0.5 border focus:outline-none"
                          style={{
                            backgroundColor: isOverridden ? c?.bg : 'rgba(255,255,255,0.05)',
                            color: isOverridden ? c?.text : 'rgba(255,255,255,0.4)',
                            borderColor: isOverridden ? c?.border : 'rgba(255,255,255,0.08)'
                          }}
                        />
                        {/* For mainboard cards: SB button (sideboard out)
                            For sideboard cards: MB button (mainboard in) */}
                        <button
                          onClick={() => toggleCopySideboard(plan, i)}
                          className={`text-[8px] px-1 py-0.5 rounded transition-colors ${card.isSideboard
                            ? (inSideboard
                              ? 'bg-cyan-500/30 text-cyan-400 border border-cyan-500/50'
                              : 'bg-white/5 text-white/30 border border-white/10 hover:bg-white/10')
                            : (inSideboard
                              ? 'bg-amber-500/30 text-amber-400 border border-amber-500/50'
                              : 'bg-white/5 text-white/30 border border-white/10 hover:bg-white/10')
                            }`}
                          title={card.isSideboard
                            ? (inSideboard ? 'In mainboard for this plan' : 'Click to bring to mainboard')
                            : (inSideboard ? 'In sideboard for this plan' : 'Click to move to sideboard')
                          }
                        >
                          {card.isSideboard ? 'MB' : 'SB'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Plan Weight + Color Totals Row */}
              <div className="flex items-center gap-1 mt-2 pt-2 border-t border-white/10">
                <span className="w-8 text-[9px] text-white/40">Σ</span>
                {plans.map(plan => {
                  // Sum MVs and color pips for copies NOT in sideboard
                  let totalWeight = 0;
                  let mainDeckCount = 0;
                  const colorCounts: Record<string, number> = { W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 };
                  const mvs = planCopyMVs[plan] || [];
                  const costs = planCopyCosts[plan] || [];
                  const sideboards = planCopySideboard[plan] || [];

                  mvs.forEach((mv, i) => {
                    if (!sideboards[i]) {
                      totalWeight += mv;
                      mainDeckCount++;
                      // Count color pips from cost string using proper hybrid-aware parser
                      const cost = costs[i] || (card.manaCost?.includes(' // ') ? card.manaCost.split(' // ')[0] : card.manaCost)?.replace(/[{}]/g, '') || '';
                      const parsed = parseManaCost(cost);
                      colorCounts['W'] += parsed.pips.W;
                      colorCounts['U'] += parsed.pips.U;
                      colorCounts['B'] += parsed.pips.B;
                      colorCounts['R'] += parsed.pips.R;
                      colorCounts['G'] += parsed.pips.G;
                      colorCounts['C'] += parsed.pips.C;
                    }
                  });

                  // Build color string (e.g., 'WW', 'UB', 'W')
                  let colorStr = '';
                  ['W', 'U', 'B', 'R', 'G', 'C'].forEach(c => {
                    colorStr += c.repeat(colorCounts[c]);
                  });

                  const c = planColors[plan];
                  const hasOverride = mvs.some((mv, i) => !sideboards[i] && mv !== baseMV);

                  return (
                    <div key={plan} className="flex-1 text-center">
                      <div
                        className="text-[10px] font-mono font-medium py-0.5 rounded flex items-center justify-center gap-1"
                        style={{
                          backgroundColor: hasOverride ? c?.bg : 'transparent',
                          color: hasOverride ? c?.text : 'rgba(255,255,255,0.5)',
                        }}
                        title={`${mainDeckCount} copies: MV ${totalWeight}${colorStr ? ` (${colorStr})` : ''}`}
                      >
                        <span>{totalWeight}</span>
                        {colorStr && (
                          <span className="text-[8px] text-white/40">|</span>
                        )}
                        {colorStr && (
                          <span className="text-[8px]" style={{ color: hasOverride ? c?.text : 'rgba(255,255,255,0.4)' }}>
                            {colorStr}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Probability Calculator */}
            {!card.isLand && (
              <div className="bg-white/5 rounded px-2 py-2 border border-white/10">
                <div className="text-[9px] text-white/40 uppercase tracking-wide mb-2">Probability Calculator</div>

                {/* Side-by-side layout: Inputs LEFT | Results RIGHT */}
                <div className="flex gap-4">
                  {/* LEFT: Inputs */}
                  <div className="flex flex-col gap-2">
                    {/* Match */}
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-white/60 w-8">Match</span>
                      <select
                        value={selectedProbPlan}
                        onChange={(e) => {
                          setSelectedProbPlan(e.target.value as 'base' | 'A' | 'B' | 'C' | 'D' | 'E');
                          setSelectedCopyIndex(0);
                        }}
                        className="bg-white/10 border border-white/10 rounded px-2 py-1 text-xs text-white"
                      >
                        <option value="base">Base</option>
                        <option value="A">Match 1</option>
                        <option value="B">Match 2</option>
                        <option value="C">Match 3</option>
                        <option value="D">Match 4</option>
                        <option value="E">Match 5</option>
                      </select>
                    </div>

                    {/* Copy */}
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-white/60 w-8">Copy</span>
                      <select
                        value={selectedCopyIndex}
                        onChange={(e) => setSelectedCopyIndex(parseInt(e.target.value))}
                        className="bg-white/10 border border-white/10 rounded px-2 py-1 text-xs text-white"
                      >
                        {(() => {
                          const mvs = selectedProbPlan === 'base'
                            ? Array(card.quantity).fill(baseMV)
                            : (planCopyMVs[selectedProbPlan] || Array(card.quantity).fill(baseMV));
                          return mvs.map((mv: number, i: number) => (
                            <option key={i} value={i}>Copy {i + 1} (MV: {mv})</option>
                          ));
                        })()}
                      </select>
                    </div>

                    {/* PLAY | DRAW */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setProbOnPlay(true)}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${probOnPlay
                          ? 'bg-cyan-500/30 text-cyan-400 border border-cyan-500/40'
                          : 'bg-white/5 text-white/40 border border-white/10'
                          }`}
                      >
                        PLAY
                      </button>
                      <button
                        onClick={() => setProbOnPlay(false)}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${!probOnPlay
                          ? 'bg-cyan-500/30 text-cyan-400 border border-cyan-500/40'
                          : 'bg-white/5 text-white/40 border border-white/10'
                          }`}
                      >
                        DRAW
                      </button>
                    </div>

                    {/* See X by T: Y */}
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-white/60">See</span>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={seenCopies}
                        onChange={(e) => setSeenCopies(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                        className="w-10 bg-white/10 border border-white/10 rounded px-1 py-0.5 text-xs text-white text-center"
                      />
                      <span className="text-[10px] text-white/60">by T:</span>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={seenByTurn}
                        onChange={(e) => setSeenByTurn(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                        className="w-10 bg-white/10 border border-white/10 rounded px-1 py-0.5 text-xs text-white text-center"
                      />
                    </div>

                    {/* Cast N by T: Z */}
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-white/60">Cast</span>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={castCopies}
                        onChange={(e) => setCastCopies(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                        className="w-10 bg-white/10 border border-white/10 rounded px-1 py-0.5 text-xs text-white text-center"
                      />
                      <span className="text-[10px] text-white/60">by T:</span>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={castByTurn}
                        onChange={(e) => setCastByTurn(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                        className="w-10 bg-white/10 border border-white/10 rounded px-1 py-0.5 text-xs text-white text-center"
                      />
                    </div>
                  </div>

                  {/* RIGHT: Results */}
                  <div className="flex-1 bg-white/5 rounded-lg p-3 border border-white/10">
                    {(() => {
                      // For 'base' plan, use card's base values. For A-E, check overrides
                      let mainCopies: number;
                      let avgMV: number;

                      if (selectedProbPlan === 'base') {
                        mainCopies = card.quantity;
                        avgMV = baseMV;
                      } else {
                        const planSBs = planCopySideboard[selectedProbPlan as 'A' | 'B' | 'C' | 'D' | 'E'] || [];
                        const mvs = planCopyMVs[selectedProbPlan as 'A' | 'B' | 'C' | 'D' | 'E'] || Array(card.quantity).fill(baseMV);
                        mainCopies = mvs.filter((_, i) => !planSBs[i]).length || card.quantity;
                        const mainMVs = mvs.filter((_, i) => !planSBs[i]);
                        avgMV = mainMVs.length > 0 ? Math.round(mainMVs.reduce((a, b) => a + b, 0) / mainMVs.length) : baseMV;
                      }

                      // Use cantrip draw value in probability
                      const cantripDraw = parseFloat(cantrip) || 0;
                      const seenCards = cardsSeenByTurn(seenByTurn, probOnPlay, cantripDraw);
                      const castCards = cardsSeenByTurn(castByTurn, probOnPlay, cantripDraw);

                      // Get the selected copy's MV for land calculation
                      const selectedCopyMV = selectedProbPlan === 'base'
                        ? baseMV
                        : (planCopyMVs[selectedProbPlan]?.[selectedCopyIndex] || baseMV);

                      // Factor in ramp: if ramp is active before cast turn, reduce lands needed
                      const rampManaBonus = (rampType !== 'None' && rampTurn && rampNet && parseInt(rampTurn) <= castByTurn)
                        ? parseInt(rampNet)
                        : 0;
                      const effectiveMVForLands = Math.max(0, selectedCopyMV - rampManaBonus);

                      const seenProb = probabilityAtLeastOne(deckSize, mainCopies, seenCards);
                      const landProb = hypergeometricCDFTail(deckSize, landCount, castCards, effectiveMVForLands);

                      // Combined playability = P(see card) × P(have lands)
                      const playabilityProb = seenProb * landProb;

                      // Use plan color or white for base
                      const planLabel = selectedProbPlan === 'base' ? 'Base' : selectedProbPlan;
                      const textColor = selectedProbPlan === 'base' ? '#94a3b8' : planColors[selectedProbPlan]?.text;

                      return (
                        <div className="flex flex-col gap-3">
                          {/* Plan Info */}
                          <div className="text-[10px] text-white/50">
                            <span style={{ color: textColor }} className="font-semibold">{planLabel}</span>
                            : {mainCopies} {mainCopies === 1 ? 'copy' : 'copies'}
                          </div>

                          {/* Selected Copy */}
                          <div className="text-[10px] text-white/40">
                            Copy {selectedCopyIndex + 1} (MV: {selectedCopyMV})
                          </div>

                          {/* Probabilities */}
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center justify-between">
                              <span className="text-[9px] text-white/40">See {seenCopies} by T{seenByTurn}</span>
                              <span className={`text-sm font-bold font-mono px-1.5 py-0.5 rounded ${getProbabilityColorClass(seenProb)} ${getProbabilityBgClass(seenProb)}`}>
                                {formatProbability(seenProb)}
                              </span>
                            </div>

                            <div className="flex items-center justify-between">
                              <span className="text-[9px] text-white/40">
                                {effectiveMVForLands}L by T{castByTurn}
                                {rampManaBonus > 0 && <span className="text-amber-400"> (−{rampManaBonus}💎)</span>}
                              </span>
                              <span className={`text-sm font-bold font-mono px-1.5 py-0.5 rounded ${getProbabilityColorClass(landProb)} ${getProbabilityBgClass(landProb)}`}>
                                {formatProbability(landProb)}
                              </span>
                            </div>
                          </div>

                          {/* Playability */}
                          <div className="pt-2 border-t border-white/10">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-white/60 font-medium">Playability</span>
                              <span className={`text-lg font-bold font-mono px-2 py-1 rounded ${getProbabilityColorClass(playabilityProb)} ${getProbabilityBgClass(playabilityProb)}`}>
                                {formatProbability(playabilityProb)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-white/10">
              {/* Make Commander button - left side */}
              <div className="flex flex-col gap-1">
                {canBeCommander && onMakeCommander && (
                  <button
                    onClick={() => {
                      onMakeCommander(card);
                      onClose();
                    }}
                    className="px-3 py-1 text-[10px] bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30 rounded flex items-center gap-1"
                    title="Move this card to the Command Zone as your commander"
                  >
                    👑 Make Commander
                  </button>
                )}
                {canBeCompanion && onMakeCompanion && (
                  <button
                    onClick={() => {
                      onMakeCompanion(card);
                      onClose();
                    }}
                    className="px-3 py-1 text-[10px] bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/30 rounded flex items-center gap-1"
                    title="Move this card to the Companion Zone"
                  >
                    🐾 Make Companion
                  </button>
                )}
                {card.isCommander && (
                  <span className="text-[10px] text-amber-400 flex items-center gap-1">
                    👑 Commander
                  </span>
                )}
                {card.isCompanion && (
                  <span className="text-[10px] text-purple-400 flex items-center gap-1">
                    🐾 Companion
                  </span>
                )}
                {/* Show Partner/Background badges for eligible cards */}
                {(card.hasPartner || card.hasPartnerWith || card.isBackground || card.choosesBackground) && !card.isCommander && (
                  <div className="flex items-center gap-1 mt-0.5">
                    {card.hasPartner && (
                      <span className="px-1.5 py-0.5 text-[8px] bg-purple-500/20 text-purple-300 rounded" title="Has Partner keyword">
                        Partner
                      </span>
                    )}
                    {card.hasPartnerWith && (
                      <span className="px-1.5 py-0.5 text-[8px] bg-pink-500/20 text-pink-300 rounded" title={`Partner with ${card.hasPartnerWith}`}>
                        Partner w/ {card.hasPartnerWith}
                      </span>
                    )}
                    {card.choosesBackground && (
                      <span className="px-1.5 py-0.5 text-[8px] bg-green-500/20 text-green-300 rounded" title="Can choose a Background">
                        +Background
                      </span>
                    )}
                    {card.isBackground && (
                      <span className="px-1.5 py-0.5 text-[8px] bg-emerald-500/20 text-emerald-300 rounded" title="Background enchantment">
                        Background
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Cancel/Save buttons OR Close prompt - right side */}
              <div className="flex items-center gap-2">
                {showClosePrompt ? (
                  /* After Save - show Close prompt */
                  <>
                    <span className="text-[10px] text-green-400 flex items-center gap-1">
                      ✓ Saved!
                    </span>
                    <span className="text-[10px] text-white/50">Close?</span>
                    <button
                      onClick={onClose}
                      className="px-2 py-1 text-[10px] bg-green-500 hover:bg-green-600 text-white rounded"
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => setShowClosePrompt(false)}
                      className="px-2 py-1 text-[10px] text-white/50 hover:text-white border border-white/20 rounded"
                    >
                      No, edit more
                    </button>
                  </>
                ) : (
                  /* Normal state - Cancel/Save */
                  <>
                    <button onClick={onClose} className="px-2 py-1 text-[10px] text-white/50 hover:text-white">
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="px-3 py-1 text-[10px] bg-cyan-500 hover:bg-cyan-600 text-white rounded"
                    >
                      Save
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div >
  );
}